import { Drawer } from '@material-tailwind/react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useAppDispatch } from '../../../store/hook';
import { useEffect, useMemo, useState } from 'react';
import {
  DEFAULT_PAGINATION,
  PAGE,
  PAGE_SIZE,
} from '../../../constants/constants';
import { getVoucherHistoryStudentList } from '../../../api/utils/lms/voucher/api';
import { openAlert } from '../../../store/features/modalSlice';
import {
  formatToKstDate,
  getErrorMessages,
} from '../../../api/utils/util-func';
import { PaginationProps } from '../../../constants/types';
import Table, { ColumnsType } from 'antd/es/table';
import { Button, Input, Modal, Pagination, Select } from 'antd';
import {
  VoucherHistoryInfo,
  VoucherHistoryStudentItem,
} from '../../../constants/types/lms/voucher/type';
import TextWinds from '../../common/TextWinds';

type VoucherHistoryStudentModalProps = {
  txId: number;
  onClose: () => void;
};

/**
 * 개편 LMS (교사) - 이용권 관리 페이지 > 이용권 내역 하단 드로어 > 이용권 내역 상세 모달
 */
const VoucherHistoryStudentModal = ({
  txId,
  onClose,
}: VoucherHistoryStudentModalProps) => {
  const { classId } = useParams();

  const [searchParams, setSearchParams] = useSearchParams();

  const dispatch = useAppDispatch();

  const [voucherHistoryInfo, setVoucherHistoryInfo] =
    useState<VoucherHistoryInfo | null>(null);
  const [voucherHistoryStudentList, setVoucherHistoryStudentList] = useState<
    VoucherHistoryStudentItem[]
  >([]);
  const [pageInfo, setPageInfo] = useState<PaginationProps>(DEFAULT_PAGINATION);

  const { page, pageSize } = useMemo(() => {
    return {
      page: +searchParams.get('page') || PAGE,
      pageSize: +searchParams.get('pageSize') || PAGE_SIZE,
    };
  }, [classId, searchParams]);

  const [searchOption, setSearchOption] = useState('name');
  const [searchValue, setSearchValue] = useState('');

  const fetchVoucherHistoryStudentList = async () => {
    try {
      const response = await getVoucherHistoryStudentList({
        classId,
        txId,
        page,
        pageSize,
        ...(searchValue
          ? {
              [searchOption]: searchValue,
            }
          : {}),
      });

      const { page: _page, students, ...rest } = response;

      setVoucherHistoryInfo(rest);
      setVoucherHistoryStudentList(students ?? []);
      setPageInfo(response.page);
    } catch (error) {
      dispatch(openAlert({ message: getErrorMessages(error) }));
    }
  };

  useEffect(() => {
    fetchVoucherHistoryStudentList();
  }, [page, pageSize]);

  const handleSearch = async () => {
    await fetchVoucherHistoryStudentList();
  };

  const columns = (
    pageInfo: PaginationProps,
  ): ColumnsType<VoucherHistoryStudentItem> => [
    {
      title: 'No.',
      key: 'index',
      render: (_, __, index) =>
        pageInfo.total - (pageInfo.page - 1) * pageInfo.pageSize - index,
      align: 'center',
      width: 60,
    },
    {
      title: '취소 여부',
      key: 'isCanceled',
      render: (_, row) => {
        switch (row.isCanceled) {
          case true:
            return '과제 배정 취소';
          default:
            return '-';
        }
      },
      align: 'center',
      className: 'min-w-[100px]',
    },
    {
      title: '학생이름',
      key: 'name',
      render: (_, row) => row.name,
      align: 'center',
      className: 'min-w-[100px]',
    },
    {
      title: '학생ID',
      key: 'uid',
      render: (_, row) => row.uid,
      align: 'center',
      className: 'min-w-[100px]',
    },
    {
      title: '반 이름',
      key: 'classGroupName',
      render: (_, row) => row.classGroupName,
      align: 'center',
      className: 'min-w-[100px]',
    },
  ];

  return (
    <Modal
      title={null}
      open={true}
      onCancel={onClose}
      footer={null}
      width={760}
      centered
      className="[&_.ant-modal-body]:p-0"
      zIndex={10000}
    >
      <div className="flex max-h-[80vh] flex-col overflow-hidden rounded-xl bg-white">
        <div className="shrink-0 pb-4">
          <TextWinds type="title_h3" color="gray9">
            이용권 사용 학생
          </TextWinds>

          <TextWinds type="content_body3" color="gray7" className="mt-1 block">
            학생 이름 또는 ID로 이용권 사용 학생을 검색할 수 있습니다.
          </TextWinds>
        </div>

        <div className="mb-3 grid gap-2 rounded-xl border border-gray2 bg-gray0 px-4 py-3 sm:grid-cols-2">
          <div>
            <TextWinds type="caption1" color="gray7">
              배정 과목
            </TextWinds>

            <TextWinds
              type="content_body3"
              color="gray9"
              className="mt-0.5 block font-bold"
            >
              {(voucherHistoryInfo && voucherHistoryInfo.todoInfo) ?? '-'}
            </TextWinds>
          </div>

          <div>
            <TextWinds type="caption1" color="gray7">
              배정 날짜
            </TextWinds>

            <TextWinds
              type="content_body3"
              color="gray9"
              className="mt-0.5 block font-bold"
            >
              {voucherHistoryInfo && voucherHistoryInfo.assignedAt
                ? formatToKstDate(
                    voucherHistoryInfo.assignedAt,
                    'YYYY.MM.DD HH:mm',
                  )
                : '-'}
            </TextWinds>
          </div>
        </div>

        <div className="shrink-0 pb-3">
          <div className="flex flex-col gap-2 sm:flex-row">
            <Select
              className="w-full sm:w-[110px] sm:shrink-0"
              options={[
                { value: 'name', label: '학생이름' },
                { value: 'uid', label: '학생ID' },
              ]}
              value={searchOption}
              onChange={(value) => setSearchOption(value)}
            />

            <Input
              type="search"
              className="min-w-0 flex-1"
              placeholder="검색어를 입력하세요"
              value={searchValue}
              onChange={(event) => setSearchValue(event.target.value)}
              onPressEnter={handleSearch}
              allowClear
            />

            <Button
              className="shrink-0 rounded-lg !border-purple5 !bg-purple5 px-5 !text-white hover:!border-purple6 hover:!bg-purple6 hover:!text-white active:!border-purple7 active:!bg-purple7"
              onClick={handleSearch}
            >
              검색
            </Button>
          </div>
        </div>

        <div className="flex shrink-0 flex-wrap items-center justify-end gap-2 pb-2">
          <div className="rounded-full bg-gray1 px-3 py-1">
            <TextWinds type="caption1" color="gray7">
              <span className="font-bold text-red5">배정 취소</span>{' '}
              <span className="font-bold text-gray9">
                {voucherHistoryInfo?.canceledCount ?? 0}
              </span>
              명
            </TextWinds>
          </div>

          <div className="rounded-full bg-gray1 px-3 py-1">
            <TextWinds type="caption1" color="gray7">
              전체 배정{' '}
              <span className="font-bold text-gray9">
                {voucherHistoryInfo?.total ?? 0}
              </span>
              명
            </TextWinds>
          </div>
        </div>

        <div className="min-h-0 flex-1">
          <div className="h-full overflow-hidden rounded-xl border border-gray2 bg-white">
            <Table
              dataSource={voucherHistoryStudentList}
              columns={columns(pageInfo)}
              rowKey={(row) => row.id}
              scroll={{ x: 'max-content', y: 360 }}
              size="small"
              pagination={false}
            />
          </div>
        </div>

        <div className="shrink-0 border-t border-gray2 pt-4">
          <Pagination
            current={page}
            pageSize={pageSize}
            total={pageInfo.total}
            showSizeChanger
            onChange={(page, pageSize) => {
              searchParams.set('page', page.toString());
              searchParams.set('pageSize', pageSize.toString());
              setSearchParams(searchParams);
            }}
            className="justify-center"
          />
        </div>
      </div>
    </Modal>
  );
};

export default VoucherHistoryStudentModal;
